import { numberToCurrency } from "helpers/string"

/**
 * Calculates the total booking amount and breakdown
 * @param {Object} bookingSelections - The booking selections object
 * @param {Object} counts - Guest counts
 * @param {Number} guideFeePercent - Guide fee percentage
 * @param {Object} options - Additional options
 * @param {String} options.paymentType - Payment type (cash, card, link, etc)
 * @param {Boolean} options.isEdit - Whether this is an edit operation
 * @param {Number} options.originalTaxAndFee - Original tax and fee amount from booking.payment_summary.tax_and_fee
 */
export const calculateBookingTotal = (bookingSelections, counts, guideFeePercent, options = {}) => {
  const { paymentType, isEdit, originalTaxAndFee } = options
  let total = 0
  const breakdown = []
  const { pricing_type, flat_rate, price_per_person, price_per_child, price_per_infant, price_per_passenger } =
    bookingSelections?.tripLength?.price || {}

  const getPrice = (specificPrice) => Number.parseFloat(specificPrice ?? 0) || 0

  const guestMapping = [
    { key: "adults", label: "Adult", price: price_per_person },
    { key: "children", label: "Child", price: price_per_child },
    { key: "infants", label: "Infant", price: price_per_infant },
    { key: "passengers", label: "Passenger", price: price_per_passenger }
  ]

  const mainCounts = counts?.main || {}
  if (pricing_type === "charge_flat_rate") {
    const flatAmount = getPrice(flat_rate)
    const hasMainGuests = Object.values(mainCounts).some((val) => val > 0)
    if (hasMainGuests) {
      total += flatAmount
      breakdown.push({
        label: "Flat Rate for Main Guests",
        amount: numberToCurrency(flatAmount)
      })
    }
  } else {
    for (const { key, label, price } of guestMapping) {
      const count = mainCounts[key] || 0
      if (count > 0) {
        const unitPrice = getPrice(price)
        const lineTotal = unitPrice * count
        total += lineTotal
        breakdown.push({
          label: `${label} x ${count}`,
          amount: numberToCurrency(lineTotal)
        })
      }
    }
  }

  const extraCounts = counts?.extra || {}
  for (const { key, label, price } of guestMapping) {
    const count = extraCounts[key] || 0
    if (count > 0) {
      const unitPrice = getPrice(price)
      const lineTotal = unitPrice * count
      total += lineTotal
      breakdown.push({
        label: `Extra ${label} x ${count}`,
        amount: numberToCurrency(lineTotal)
      })
    }
  }

  // Determine the fee percentage to use based on payment type and mode
  let effectiveFeePercent = guideFeePercent
  let isCashPayment = false
  let fixedTaxAmount = null

  if (paymentType === "cash") {
    isCashPayment = true
    if (isEdit && originalTaxAndFee !== undefined && originalTaxAndFee !== null) {
      // For edit mode with cash payment, use the original fixed tax amount
      fixedTaxAmount = Number(originalTaxAndFee)
      effectiveFeePercent = 0 // Don't calculate percentage-based fee
    } else {
      // For add mode with cash payment, use 0% tax & fees
      effectiveFeePercent = 0
    }
  }

  // Always show Tax & Fees line if we have a total and either:
  // - There's a fee percentage, OR
  // - It's cash payment (to show fixed amount or $0.00)
  if (total > 0 && (effectiveFeePercent || isCashPayment)) {
    let taxAmount
    if (fixedTaxAmount !== null) {
      // Use the fixed original tax amount for edit mode with cash
      taxAmount = fixedTaxAmount
    } else {
      // Calculate based on percentage
      taxAmount = (total * (effectiveFeePercent || 0)) / 100
    }

    total += taxAmount
    breakdown.push({
      label: `Tax & Fees`,
      amount: numberToCurrency(taxAmount)
    })
  }

  return { total, breakdown }
}
